import React, { forwardRef, useCallback } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import InfiniteLoader from 'react-window-infinite-loader';
import { loadMoreItemsFromSections, isItemLoaded } from '../shared/infinite.utils';

const getInfiniteLoaderContent = (options) =>
  forwardRef((props, ref) => {
    const { Layout, useOverscanForLoading, onItemsRendered, ...layoutProps } = props;
    const { calculateLayoutIndexes } = options;

    const onLayoutItemsRendered = useCallback(
      (data) => {
        const indexes = calculateLayoutIndexes ? calculateLayoutIndexes(data, useOverscanForLoading) : data;
        //call onItemsRendered from InfiniteLoader, so it can load more if needed
        return onItemsRendered(indexes);
      },
      [calculateLayoutIndexes, useOverscanForLoading, onItemsRendered]
    );

    return <Layout {...layoutProps} onItemsRendered={onLayoutItemsRendered} ref={ref} />;
  });

export const getLayoutWrapper = (options) => (props) => {
  const { InfiniteLoaderContent, ...externalProps } = props;
  const layoutProps = options.getLayoutDataProps(props);

  const { sections } = layoutProps;

  const isLayoutItemLoaded = useCallback(
    (index) => {
      return isItemLoaded(index, sections);
    },
    [sections]
  );

  const loadLayoutItems = useCallback(
    (start, end) => {
      return loadMoreItemsFromSections(sections, start, end);
    },
    [sections]
  );

  return (
    <InfiniteLoader
      itemCount={layoutProps.total || props.initialCount}
      isItemLoaded={isLayoutItemLoaded}
      loadMoreItems={loadLayoutItems}
      threshold={props.threshold}
    >
      {(infiniteLoaderProps) => <InfiniteLoaderContent {...externalProps} {...layoutProps} {...infiniteLoaderProps} />}
    </InfiniteLoader>
  );
};

export default (options) => (LayoutComponent) => {
  const Wrapper = getLayoutWrapper(options);
  const InfiniteLoaderContent = getInfiniteLoaderContent(options);

  return (props) => {
    return <AutoSizer>{(size) => <Wrapper {...props} {...size} Layout={LayoutComponent} InfiniteLoaderContent={InfiniteLoaderContent} />}</AutoSizer>;
  };
};
